import { useMemo, useState } from "react";
import { MdArrowUpward, MdArrowDownward, MdUnfoldMore, MdChevronLeft, MdChevronRight } from "react-icons/md";
import { AdminTableSkeleton } from "../AdminSkeleton/AdminSkeleton";
import { AdminEmptyState } from "../AdminEmptyState/AdminEmptyState";
import "./adminTable.css";

// The one table implementation for the whole app. Every list page (Orders,
// Products, Customers, Staff, Categories, Coupons, Sales, Reviews,
// Messages, Activity Log) renders through this component instead of each
// page hand-rolling its own card grid or reaching for MUI's DataGrid.
//
// AdminTable owns presentation - sticky header, row hover, selection,
// pagination, loading/empty states. Filtering and sorting *logic* stays
// with the page (it already varies per page - price sorts differently
// from order status) - AdminTable just renders the sort indicator and
// calls back via onSortChange.
export const AdminTable = ({
  columns,
  rows,
  rowKey,
  isLoading = false,
  emptyIcon,
  emptyTitle = "Nothing here yet",
  emptyDescription,
  emptyAction,
  selectable = false,
  selectedKeys,
  onSelectionChange,
  bulkActionsBar,
  sortKey,
  sortDirection = "asc",
  onSortChange,
  pageSize = 10,
  onRowClick,
}) => {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => rows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [rows, currentPage, pageSize]
  );

  const allPageKeysSelected =
    selectable && pageRows.length > 0 && pageRows.every((r) => selectedKeys?.has(rowKey(r)));

  const toggleAll = () => {
    if (!onSelectionChange) return;
    const next = new Set(selectedKeys);
    if (allPageKeysSelected) {
      pageRows.forEach((r) => next.delete(rowKey(r)));
    } else {
      pageRows.forEach((r) => next.add(rowKey(r)));
    }
    onSelectionChange(next);
  };

  const toggleOne = (key) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange(next);
  };

  if (isLoading) {
    return (
      <div className="admin-table-wrap">
        <AdminTableSkeleton columns={columns.length} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="admin-table-wrap">
        <AdminEmptyState
          icon={emptyIcon}
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
        />
      </div>
    );
  }

  return (
    <div className="admin-table-wrap">
      {selectable && selectedKeys?.size > 0 && bulkActionsBar && (
        <div className="admin-table-bulkbar">
          <span>{selectedKeys.size} selected</span>
          <div className="admin-table-bulkbar__actions">{bulkActionsBar}</div>
        </div>
      )}
      <div className="admin-table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              {selectable && (
                <th className="admin-table__checkbox-col">
                  <input
                    type="checkbox"
                    checked={allPageKeysSelected}
                    onChange={toggleAll}
                    aria-label="Select all rows on this page"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{ width: col.width }}
                  className={col.align === "right" ? "admin-table__col--right" : ""}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      className="admin-table__sort-btn"
                      onClick={() => onSortChange?.(col.key)}
                      aria-label={`Sort by ${col.header}`}
                    >
                      {col.header}
                      {sortKey === col.key ? (
                        sortDirection === "asc" ? <MdArrowUpward /> : <MdArrowDownward />
                      ) : (
                        <MdUnfoldMore className="admin-table__sort-icon--idle" />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              const key = rowKey(row);
              const selected = selectedKeys?.has(key);
              return (
                <tr
                  key={key}
                  className={`${selected ? "admin-table__row--selected" : ""} ${onRowClick ? "admin-table__row--clickable" : ""}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {selectable && (
                    <td className="admin-table__checkbox-col" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={Boolean(selected)}
                        onChange={() => toggleOne(key)}
                        aria-label="Select row"
                      />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className={col.align === "right" ? "admin-table__col--right" : ""}>
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="admin-table-pagination">
          <span className="admin-table-pagination__summary">
            Page {currentPage} of {totalPages} &middot; {rows.length} total
          </span>
          <div className="admin-table-pagination__controls">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <MdChevronLeft />
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              <MdChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
